import { useEffect, useState } from 'react'
import { api } from '~/services/api'
import { PublicKey } from '@solana/web3.js'
import { TOKEN_2022_PROGRAM_ID } from '@solana/spl-token'

export type GameData = {
  id: string;
  game: {
    id: string;
    name: string;
    category: string;
    categoryColor: string;
    img: string;
    description: string;
  };
  token: {
    address: string;
    symbol: string;
    name: string;
    price: number;
    img: string;
    decimals: number;
    program: PublicKey;
    marketCap: string;
    transactions: string;
    volume: {
      total: string;
      '5m': string;
      '1h': string;
      '6h': string;
      '24h': string;
    };
    fdv: string;
    liquidity: string;
    xpPrice: number;
    devPool: number;
    supply: number;
  };
  github: {
    pr: string;
    commits: string;
    stars: string;
    link: string;
  };
  tasks: Array<{
    id: string;
    title: string;
    description: string;
    difficulty: {
      value: string;
      label: string;
    };
    items: Array<{
      id: string;
      type: string;
      title: string;
      description: string;
      link?: string;
      commits?: string;
      comments?: string;
      votes: [number, number];
    }>;
  }>;
}

export function useGameDataByRepository({ repositoryName }: { repositoryName: string }) {
  const [game, setGame] = useState<GameData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const gameData = await api.games.getByRepositoryName(repositoryName)
        
        // Transform API data to match component expectations
        const transformedGame: GameData = {
          id: gameData.dagId,
          game: {
            id: gameData.dagId,
            name: gameData.dagName,
            category: 'Game', // Default category since not provided by API
            categoryColor: '#60e25e', // Default color since not provided by API
            img: `https://picsum.photos/seed/${gameData.dagId}/800/400`, // Placeholder image
            description: 'A decentralized game on the blockchain', // Default description
          },
          token: {
            address: gameData.tokenAddress,
            symbol: gameData.dagName.slice(0, 6), // Using first 6 chars of DAG name as symbol
            name: gameData.dagName, // Using DAG name as token name
            price: 0.001, // Default price
            img: `https://picsum.photos/seed/${gameData.dagId}/200/200`, // Placeholder image
            decimals: 6, // Default decimals for Solana tokens
            program: TOKEN_2022_PROGRAM_ID,
            marketCap: '0', // Not provided by API
            transactions: '0', // Not provided by API
            volume: {
              total: '0',
              '5m': '0',
              '1h': '0',
              '6h': '0',
              '24h': '0',
            },
            fdv: '0', // Not provided by API
            liquidity: '0', // Not provided by API
            xpPrice: 0, // Not provided by API
            devPool: 0, // Not provided by API
            supply: 0, // Not provided by API
          },
          github: {
            pr: '0', // Not provided by API yet
            commits: '0', // Not provided by API yet
            stars: '0', // Not provided by API yet
            link: gameData.githubRepositoryUrl,
          },
          tasks: [], // Empty array for now, will be populated when we implement task fetching
        }

        setGame(transformedGame)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch game data'))
      } finally {
        setLoading(false)
      }
    }

    fetchGame()
  }, [repositoryName])

  return {
    game,
    isLoading: loading,
    error,
  }
} 